import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Common components
import AppBar from "../../../common/AppBar";
import Loader from "../../../common/Loader";
import Snackbar from "../../../common/Snackbar";
import { SaveModal } from "../../../common/Modals";
import {
  VersionHistoryPopup,
  getVersionHistoryAuditPreviewPath,
} from "../../../common/VersionHistoryPopup";
import { useVersionHistoryDataFromNodes } from "../../../common/VersionHistoryPopup/useVersionHistoryDataFromNodes.js";

// Form components
import { TextField, FileUpload } from "../../../common/FormFields";
import ShortDescriptionEditor from "../../../common/RichTextEditor/ShortDescriptionEditor";

// Redux actions
import {
  createAttachment,
  updateAttachment,
} from "../../../redux/attachmentSlice";
import { openPreview } from "../../../redux/previewSlice";

// Utilities
import { generateSHA256 } from "../../../util/generateSHA256";
import { getAuditTrailData } from "../../../util/getAuditTrailData";
import { useUnsavedChangesBlocker } from "../../../hooks/useUnsavedChangesBlocker";
import { buildPermissionContext } from "../../../util/permissions";
import { cleanBaselineHtml } from "../../../util/lexical";
import { setTaskReference } from "../../Tasks/Uitlities";

// Custom hooks
import useAttachmentData from "../hooks/useAttachmentData";
import useAttachmentPersistence from "../hooks/useAttachmentPersistence";
import { BASE_URL } from "../../../config";

/** Global "Attachment N" index — same traversal as ViewChildNode / ChildrenRenderer. */
function computeGlobalMediaOrdinalForAttachment({
  allRevisionNodes,
  section_id_ref,
  step_id_ref,
  child_node_id,
}) {
  const nodeType = "attachment";
  if (
    !child_node_id ||
    !Array.isArray(allRevisionNodes) ||
    !section_id_ref ||
    !step_id_ref
  ) {
    return null;
  }

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const sortedSections = [...allRevisionNodes].sort((a, b) => {
    const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  let counter = 0;
  const matchesId = (c) =>
    c.tree_node_id === child_node_id ||
    c.detail?.canvas_tree_node_id === child_node_id ||
    c.detail?.attachment_tree_node_id === child_node_id;

  for (let i = 0; i < sortedSections.length; i++) {
    const section = sortedSections[i];
    for (let j = 0; j < (section.steps?.length || 0); j++) {
      const step = section.steps[j];
      const stepChildren =
        step.children?.filter((c) => c.type === nodeType) || [];

      const currentStepChild = stepChildren.find(matchesId);
      if (currentStepChild) {
        const sortedStepChildren = [...stepChildren].sort((a, b) => {
          const orderA = a.sibling_order || 0;
          const orderB = b.sibling_order || 0;
          return orderA - orderB;
        });
        const currentChildIndex = sortedStepChildren.findIndex(matchesId);
        counter += currentChildIndex;
        return counter + 1;
      }
      counter += stepChildren.length;

      for (let k = 0; k < (step.substeps?.length || 0); k++) {
        const substep = step.substeps[k];
        const substepChildren =
          substep.children?.filter((c) => c.type === nodeType) || [];
        const currentSubstepChild = substepChildren.find(matchesId);
        if (currentSubstepChild) {
          const sortedSubstepChildren = [...substepChildren].sort((a, b) => {
            const orderA = a.sibling_order || 0;
            const orderB = b.sibling_order || 0;
            return orderA - orderB;
          });
          const currentChildIndex =
            sortedSubstepChildren.findIndex(matchesId);
          counter += currentChildIndex;
          return counter + 1;
        }
        counter += substepChildren.length;
      }
    }
  }
  return counter + 1;
}

/**
 * AttachmentForm - Optimized attachment form component
 *
 * Key improvements:
 * - Custom hooks for data management
 * - Reduced location state dependencies
 * - Better error handling
 * - Cleaner state management
 * - Improved persistence strategy
 */
const AttachmentForm = ({
  // Core props
  mode,

  // URL parameters
  partnumber,
  revision,
  partId,
  revisionId,
  section_id_ref,
  step_id_ref,
  substep_id_ref,
  node_id_ref,
  attachment_id_ref,
  // Version history
  fromVersionHistory,

  // Optional state data
  status,
  description,
  type,
  attachmentCount,
  richCount,
  canvasCount,
  videoCount,
  formCount,
  stepNumber,
  data,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  // Use custom hooks for data management
  const {
    loading,
    attachmentData,
    versionId,
    procedureId,
    lastSavedText,
    pageTitle,
    breadcrumbText,
    siblingOrder,
    allNodesData,
    sectionId,
    stepId,
    substepId,
    currentStatus,
    item_code_type,
  } = useAttachmentData({
    mode,
    id: attachment_id_ref,
    partnumber,
    revision,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
    substep_id_ref,
    node_id_ref,
  });

  const {
    formData,
    setFormData,
    files,
    setFiles,
    removedAttachmentIds,
    setRemovedAttachmentIds,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    saveForPreview,
    clearStoredData,
  } = useAttachmentPersistence({
    mode,
    id: attachment_id_ref,
    attachmentData,
  });

  // Debug: Log files and removedAttachmentIds when they change
  useEffect(() => {
  }, [files]);

  useEffect(() => {
  }, [removedAttachmentIds]);

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false); // Flag to disable blocker during navigation
  const [showModal, setShowModal] = useState(false);
  const [comment, setComment] = useState("");
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  // Undo-based dirty tracking for the Lexical short-description editor.
  // CAN_UNDO_COMMAND fires `true` when the user has genuinely typed something
  // and `false` when they undo all the way back to the original state.
  const [isShortDescriptionDirty, setIsShortDescriptionDirty] = useState(false);

  // Stable callback passed to ShortDescriptionEditor as onDirtyChange.
  const handleShortDescriptionDirtyChange = useCallback(
    (dirty) => setIsShortDescriptionDirty(dirty),
    []
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  // Redux: attachment hook loads version history for last-saved text; popup uses mergedVersionHistories.

  const attachmentRouteChildId =
    attachmentData?.tree_node_id || attachment_id_ref || null;

  const attachmentVersionHistoryMatchIds = useMemo(() => {
    const fromAttachments = Array.isArray(attachmentData?.detail?.attachments)
      ? attachmentData.detail.attachments.flatMap((a) =>
          [a?.attachment_tree_node_ref, a?.attachment_id].filter(Boolean),
        )
      : [];
    return [
      attachmentRouteChildId,
      attachment_id_ref,
      attachmentData?.tree_node_id,
      attachmentData?.attachment_id_ref,
      attachmentData?.attachment_tree_node_id,
      attachmentData?.detail?.procedure_tree_ref,
      attachmentData?.detail?.attachment_id,
      attachmentData?.detail?.attachment_tree_node_id,
      attachmentData?.detail?.element_id,
      ...fromAttachments,
    ].filter(Boolean);
  }, [
    attachmentRouteChildId,
    attachment_id_ref,
    attachmentData?.tree_node_id,
    attachmentData?.attachment_id_ref,
    attachmentData?.attachment_tree_node_id,
    attachmentData?.detail?.procedure_tree_ref,
    attachmentData?.detail?.attachment_id,
    attachmentData?.detail?.attachment_tree_node_id,
    attachmentData?.detail?.element_id,
    attachmentData?.detail?.attachments,
  ]);

  const attachmentMediaOrdinalForNodeHistory = useMemo(
    () =>
      computeGlobalMediaOrdinalForAttachment({
        allRevisionNodes: allNodesData,
        section_id_ref,
        step_id_ref,
        child_node_id: attachmentRouteChildId,
      }),
    [
      allNodesData,
      section_id_ref,
      step_id_ref,
      attachmentRouteChildId,
    ],
  );

  const {
    mergedVersionHistories,
    allVersionHistories,
    versionHistoryTableLoading,
  } = useVersionHistoryDataFromNodes({
    isOpen: showVersionHistory && mode === "edit",
    revisionId,
    partId,
    allRevisionNodes: allNodesData,
    fallbackVersionId:
      attachmentData?.detail?.version_id_ref ||
      attachmentData?.version_id_ref ||
      null,
    nodesReady:
      mode === "edit" &&
      !loading &&
      Array.isArray(allNodesData) &&
      allNodesData.length > 0,
  });

  // Navigation protection - disable during navigation or when explicitly disabled.
  // Short description dirty state comes from Lexical's undo history to avoid
  // false positives from the editor's own programmatic initialisation.
  const shouldBlockNavigation =
    (hasUnsavedChanges || isShortDescriptionDirty) && !isLoading && !isNavigating;

  // Use a ref to immediately disable the blocker during navigation
  const isNavigatingRef = useRef(false);

  useUnsavedChangesBlocker(shouldBlockNavigation && !isNavigatingRef.current);

  // Custom function to navigate without triggering unsaved changes alert
  const navigateWithoutAlert = (url) => {

    // Immediately set the ref to disable the blocker
    isNavigatingRef.current = true;

    try {
      // Use window.location.href for most reliable navigation
      window.location.href = url;

    } catch (error) {
      console.error("❌ window.location.href failed:", error);

      // Fallback to window.location.replace
      try {
        window.location.replace(url);
      } catch (replaceError) {
        console.error("❌ window.location.replace also failed:", replaceError);
        // Final fallback - form submission
        const form = document.createElement('form');
        form.method = 'GET';
        form.action = url;
        form.style.display = 'none';
        document.body.appendChild(form);
        form.submit();
      }
    }
  };

  // Build full URL with basename for window.location (workaround for React Router location sync)
  const buildFullViewChildNodeUrl = (childNodeId) => {
    const pathname = window.location.pathname;
    const basename = pathname.split("/").filter(Boolean)[0];
    const base = basename ? `/${basename}` : "";
    const path = `/view-childnode/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${childNodeId}${substep_id_ref ? `?substep_id_ref=${substep_id_ref}` : ""}`;
    const fullUrl = `${window.location.origin}${base}${path}`;
    return fullUrl;
  };

  // Snackbar handlers
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // File handling
  const handleFileChange = (selectedFiles) => {
    setFiles((prev) => [...prev, ...selectedFiles]);
    setHasUnsavedChanges(true);
  };

  const handleDeleteFile = (indexToRemove) => {
    const fileToRemove = files[indexToRemove];

    if (
      mode === "edit" &&
      fileToRemove?.isExisting &&
      fileToRemove?.attachment_id
    ) {
      setRemovedAttachmentIds((prev) => {
        const newArray = [...prev, fileToRemove.attachment_id];
        return newArray;
      });
    }

    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setHasUnsavedChanges(true);
  };

  // Helper function to check if short description contains images
  const hasImagesInShortDescription = () => {
    if (!formData.shortDescription) return false;
    
    // Check for base64 images in the content
    const base64ImageRegex = /<img[^>]+src="data:image\/[^>]+>/;
    return base64ImageRegex.test(formData.shortDescription);
  };

  // Save handlers
  const handleSaveClick = () => {
    const hasFiles = files && files.length > 0;
    const hasRichTextImages = hasImagesInShortDescription();
    
    if (hasFiles || hasRichTextImages) {
      setShowModal(true);
    } else {
      showSnackbar("Please upload a file or add an image to the short description before saving.", "error");
    }
  };

  const handleCloseModal = () => setShowModal(false);

  const handleConfirmSave = async () => {
    if (!comment) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }

    const hasFiles = files && files.length > 0;
    const hasRichTextImages = hasImagesInShortDescription();
    
    if (!hasFiles && !hasRichTextImages) {
      showSnackbar("Please upload a file or add an image to the short description before saving.", "error");
      return;
    }

    setIsLoading(true);

    try {
      // Upload files to S3 and get URLs
      const attachmentUrls = [];
      
      // Only process file uploads if there are actual files
      if (hasFiles) {
        for (const file of files) {
          let fileToUpload = null;

          if (file instanceof File) {
            fileToUpload = file;
          } else if (file.base64Data) {
            try {
              const res = await fetch(file.base64Data);
              const blob = await res.blob();
              fileToUpload = new File([blob], file.name, { type: file.type });
            } catch (err) {
              console.error("Failed converting base64 to File:", err);
              continue;
            }
          }

          if (fileToUpload) {
            try {
              // Generate unique filename for S3 upload
              const timestamp = new Date().getTime();
              const fileExtension = fileToUpload.name.split(".").pop() || "file";
              const fileName = `attachment-${timestamp}.${fileExtension}`;
              const fileType = fileToUpload.type || `application/octet-stream`;


              // Upload to S3 and get the URL
              const s3Url = await uploadToS3(fileToUpload, fileName, fileType);


              // Collect S3 URL for attachment array
              attachmentUrls.push(s3Url);
            } catch (uploadError) {
              console.error("❌ Error uploading file to S3:", uploadError);
              showSnackbar(
                "Error uploading file to S3: " +
                (uploadError?.message || "Unknown error"),
                "error"
              );
              setIsLoading(false);
              return;
            }
          }
        }
      }

      if (mode === "add") {
        // Create JSON payload for add mode
        const response = await dispatch(
          createAttachment({
            jsonData: {
              short_description: formData.shortDescription,
              revision_id_ref: revisionId,
              section_id_ref: sectionId,
              step_id_ref: stepId,
              part_required_id_ref: partId,
              substep_id_ref: substepId,
              sibling_order: (siblingOrder || 1).toString(),
              attachment: attachmentUrls,
            },
            comment,
            version_content: {
              action_to_show: `Save ${pageTitle} for ${partnumber}, Rev ${revision}`,
              partNumber: partnumber,
              revision,
              status: currentStatus,
              item_code_type: item_code_type,
              type: "attachment",
            },
            allNodesData,
            partnumber,
            revision,
          })
        ).unwrap();

        showSnackbar("Attachment created successfully!", "success");

        // Immediately disable unsaved changes blocker by setting both flags and ref
        setHasUnsavedChanges(false);
        setIsShortDescriptionDirty(false);
        setIsNavigating(true);
        isNavigatingRef.current = true; // Immediately disable blocker
        clearStoredData();

        // Navigate to success page
        const attachmentNumber = parseInt(pageTitle.split(" ")[1]);

        // Extract the correct tree_node_id from response - handle different response structures
        let childNodeId;
        if (response?.results?.ProcedureTree?.tree_node_id) {
          childNodeId = response.results.ProcedureTree.tree_node_id;
        } else if (response?.results?.tree_node_id) {
          childNodeId = response.results.tree_node_id;
        } else if (response?.tree_node_id) {
          childNodeId = response.tree_node_id;
        } else if (response?.results?.ProcedureTree?.id) {
          childNodeId = response.results.ProcedureTree.id;
        } else if (response?.results?.id) {
          childNodeId = response.results.id;
        } else {
          console.error("❌ Could not find tree_node_id in response:", response);
          showSnackbar("Attachment created but navigation failed - please navigate manually", "warning");
          setIsLoading(false);
          return;
        }

        const navigationUrl = buildFullViewChildNodeUrl(childNodeId);

        // Use React Router navigate instead of window.location to avoid beforeunload event
        setTimeout(() => {
          
          // Try primary navigation
          navigateWithoutAlert(navigationUrl);
          
          // Fallback: if navigation doesn't work within 2 seconds, try alternative
          setTimeout(() => {
            if (window.location.pathname.includes('/attachment/add') || window.location.pathname.includes('/attachment/edit')) {
              // Try a simpler navigation URL
              const fallbackUrl = `/view-childnode/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${childNodeId}`;
              window.location.href = fallbackUrl;
            }
          }, 2000);
        }, 100); // Reduced delay for faster navigation
      } else {
        // Edit mode - upload new files and collect URLs
        const newAttachmentUrls = [];
        
        // Only process file uploads if there are actual files
        if (hasFiles) {
          for (const file of files) {
          if (file.isExisting) {
            // Skip existing files - they are handled via old_attachments
          } else {
            let fileToUpload = null;

            if (file instanceof File) {
              fileToUpload = file;
            } else if (file.base64Data) {
              // For files restored from localStorage, convert back to File object
              try {
                const base64Response = await fetch(file.base64Data);
                const blob = await base64Response.blob();
                fileToUpload = new File([blob], file.name, { type: file.type });
              } catch (error) {
                console.error("Error converting base64 file:", error);
                showSnackbar(
                  `Error processing file ${file.name}: ${error.message}`,
                  "error"
                );
                setIsLoading(false);
                return;
              }
            } else {
              continue;
            }

            if (fileToUpload) {
              try {
                // Generate unique filename for S3 upload
                const timestamp = new Date().getTime();
                const fileExtension =
                  fileToUpload.name.split(".").pop() || "file";
                const fileName = `attachment-${timestamp}.${fileExtension}`;
                const fileType =
                  fileToUpload.type || `application/octet-stream`;


                // Upload to S3 and get the URL
                const s3Url = await uploadToS3(
                  fileToUpload,
                  fileName,
                  fileType
                );


                // Collect S3 URL for attachment array
                newAttachmentUrls.push(s3Url);
              } catch (uploadError) {
                console.error("❌ Error uploading file to S3:", uploadError);
                showSnackbar(
                  "Error uploading file to S3: " +
                  (uploadError?.message || "Unknown error"),
                  "error"
                );
                setIsLoading(false);
                return;
              }
            }
          }
        }
        }

        // Get old_attachments for edit mode (filter out removed ones)
        const oldAttachments = [];
        if (mode === "edit" && attachmentData) {
          // Check if we have attachments data in the node (using correct nested path)
          if (
            attachmentData.detail?.attachments &&
            Array.isArray(attachmentData.detail.attachments)
          ) {
            const filteredAttachments = attachmentData.detail.attachments
              .filter(
                (item) =>
                  !removedAttachmentIds.includes(item.detail?.attachment_id)
              )
              .map((a) => a.attachment);

            oldAttachments.push(...filteredAttachments);
          } else {
            // If no attachments data in node, send empty array
          }
        }

        // Check if there are no changes (no new files uploaded and no files deleted)
        const hasNoChanges =
          newAttachmentUrls.length === 0 && removedAttachmentIds.length === 0;

        // Prepare attachment payload
        // - No changes: empty array
        // - New files added: send new file URLs in attachment array
        const attachmentPayload = hasNoChanges ? [] : newAttachmentUrls;

        // Prepare old_attachments payload
        // - Always contains existing old attachments (filtered to exclude deleted ones)
        // - Sent together with attachment array (old files in old_attachments, new files in attachment)
        const oldAttachmentsPayload = oldAttachments;

        if (hasNoChanges) {
        } else {
        }

        // Create version_content for update audit trail
        const updateVersionContent = {
          action_to_show: `Update ${pageTitle} for ${partnumber}, Rev ${revision}`,
          partNumber: partnumber,
          revision,
          status: currentStatus,
          item_code_type: item_code_type,
          type: "attachment",
        };

        // Create JSON payload for edit mode
        const jsonPayload = {
          short_description: formData.shortDescription,
          revision_id_ref: revisionId,
          section_id_ref: sectionId,
          step_id_ref: stepId,
          part_required_id_ref: partId,
          substep_id_ref: substepId,
          sibling_order: (
            attachmentData.sibling_order ||
            attachmentData.sibling_order?.toString() ||
            "1"
          ).toString(),
          attachment_tree_node_id:
            attachmentData.detail.attachment_tree_node_id,
          procedure_tree_id: attachment_id_ref,
          remove_attachment_id: removedAttachmentIds,
          attachment: attachmentPayload,
        };

        // Always add old_attachments key when there are old attachments
        if (
          oldAttachmentsPayload !== null &&
          oldAttachmentsPayload.length > 0
        ) {
          jsonPayload.old_attachments = oldAttachmentsPayload;
        }

        const response = await dispatch(
          updateAttachment({
            id: attachment_id_ref,
            jsonData: jsonPayload,
            comment: comment || `Updated ${pageTitle}`,
            version_content: updateVersionContent,
            allNodesData,
            partnumber,
            revision,
            parent_version:
              versionId != null && String(versionId).trim() !== ""
                ? String(versionId).trim()
                : null,
          })
        ).unwrap();

        showSnackbar("Attachment updated successfully!", "success");

        // Immediately disable unsaved changes blocker by setting both flags and ref
        setHasUnsavedChanges(false);
        setIsShortDescriptionDirty(false);
        setIsNavigating(true);
        isNavigatingRef.current = true; // Immediately disable blocker
        setRemovedAttachmentIds([]);
        clearStoredData();


        const navigationUrl = buildFullViewChildNodeUrl(attachment_id_ref);

        // Use React Router navigate instead of window.location to avoid beforeunload event
        setTimeout(() => {
          
          // Try primary navigation
          navigateWithoutAlert(navigationUrl);
          
          // Fallback: if navigation doesn't work within 2 seconds, try alternative
          setTimeout(() => {
            if (window.location.pathname.includes('/attachment/add') || window.location.pathname.includes('/attachment/edit')) {
              // Try a simpler navigation URL
              const fallbackUrl = `/view-childnode/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${attachment_id_ref}`;
              window.location.href = fallbackUrl;
            }
          }, 2000);
        }, 100); // Reduced delay for faster navigation
      }
    } catch (error) {
      console.error(
        `Error ${mode === "add" ? "creating" : "updating"} attachment:`,
        error
      );
      showSnackbar(
        `Error ${mode === "add" ? "creating" : "updating"} attachment: ${error?.message
        }`,
        "error"
      );
    } finally {
      setIsLoading(false);
      setIsNavigating(false); // Reset navigation flag
      setShowModal(false);
    }
  };

  // Preview functionality
  const handlePreviewClick = async () => {
    // Validate mandatory fields before showing preview
    const hasFiles = files && files.length > 0;
    const hasRichTextImages = hasImagesInShortDescription();
    
    if (!hasFiles && !hasRichTextImages) {
      showSnackbar("Please upload a file or add an image to the short description before previewing.", "error");
      return;
    }

    // For edit mode, save draft to IndexedDB for persistence
    if (mode === "edit") {
      try {
        await saveForPreview();
      } catch (err) {
        // Continue with preview even if save fails
      }
    }
    // Note: For add mode, we don't need to save to localStorage since
    // we're passing data directly via Redux (avoids quota errors)

    // Convert files to base64 for preview
    const base64Files = await Promise.all(
      files.map(async (file) => {
        if (file.isExisting) {
          return {
            name: file.name,
            type: "application/octet-stream",
            attachment: file.url,
            isExisting: true,
            attachment_id: file.attachment_id,
          };
        }

        if (file.base64Data) {
          return {
            name: file.name,
            type: file.type || "application/octet-stream",
            base64: file.base64Data,
            attachment: file.base64Data,
          };
        }

        if (file instanceof File) {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                name: file.name,
                type: file.type,
                base64: reader.result,
                attachment: reader.result,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        }

        return file;
      })
    );

    // Construct draft child for attachment
    const childId =
      mode === "edit" ? attachment_id_ref : `draft-attachment-${Date.now()}`;
    const draftChild = {
      tree_node_id: childId,
      type: "attachment",
      mode: mode, // "add" or "edit"
      isDraft: true,
      sibling_order: (siblingOrder || 1).toString(),
      section_id_ref: section_id_ref,
      step_id_ref: step_id_ref,
      substep_id_ref: substep_id_ref || null,
      detail: {
        short_description: formData.shortDescription,
        attachments: base64Files.map((f) => ({
          attachment: f.base64 || f.attachment || f.url || "",
          detail: {
            name: f.name,
            file_type: f.type,
          },
        })),
      },
    };

    dispatch(
      openPreview({
        partId,
        revisionId,
        draftChild,
      })
    );
  };

  // Upload to S3 helper function
  const uploadToS3 = async (data, fileName, fileType) => {
    try {
      // Get authorization token from localStorage or other storage
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("auth_token") ||
        sessionStorage.getItem("token") ||
        "";

      if (!token) {
        throw new Error("No authorization token found");
      }

      // 1. Get presigned URL from backend
      const response = await fetch(
        `${BASE_URL}upload/get-upload-urls`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            folder: "attachment",
            files: [{ fileName, fileType }],
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get upload URL: ${response.status}`);
      }

      const responseData = await response.json();
      const { uploadUrl, fileUrl } = responseData.files[0];

      // 2. Convert to blob if needed
      let blob;

      if (data instanceof Blob || data instanceof File) {
        blob = data;
      } else if (typeof data === "string") {
        if (!data.startsWith("data:")) {
          throw new Error("Invalid data format");
        }

        try {
          const base64Response = await fetch(data);
          if (!base64Response.ok) {
            throw new Error(
              `Failed to convert base64 to blob: ${base64Response.status}`
            );
          }
          blob = await base64Response.blob();
        } catch (blobError) {
          throw new Error(`Base64 conversion failed: ${blobError.message}`);
        }
      } else {
        throw new Error("Invalid data type provided");
      }

      // Validate blob size
      if (blob.size === 0) {
        throw new Error("Generated blob is empty");
      }

      const sizeInMB = blob.size / (1024 * 1024);
      if (sizeInMB > 100) {
        throw new Error(`File too large: ${sizeInMB.toFixed(2)}MB (max 100MB)`);
      }

      // 3. Upload to S3 using presigned URL with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes

      try {
        const s3Response = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": fileType,
          },
          body: blob,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!s3Response.ok) {
          throw new Error(`S3 upload failed: ${s3Response.status}`);
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === "AbortError") {
          throw new Error("S3 upload timed out after 5 minutes");
        }
        throw fetchError;
      }

      return fileUrl;
    } catch (error) {
      throw error;
    }
  };

  // AppBar back handler
  const handleAppBarBack = useCallback(() => {
    // Check for unsaved changes and show confirmation if needed
    if ((hasUnsavedChanges || isShortDescriptionDirty) && !isLoading) {
      const confirmed = window.confirm("You have unsaved changes. Are you sure you want to leave?");
      if (!confirmed) {
        return; // Don't navigate if user cancels
      }

      // User confirmed, disable blocker and navigate
      setHasUnsavedChanges(false);
      setIsShortDescriptionDirty(false);
      setIsNavigating(true);
      isNavigatingRef.current = true;
    }

    // Force a page reload to ensure proper navigation
    setTimeout(() => {
      window.history.back();

      // Force page reload after navigation to ensure clean state
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }, 100);
  }, [hasUnsavedChanges, isShortDescriptionDirty, isLoading]);

  // Set task reference for edit mode
  useEffect(() => {
    if (mode === "edit" && attachmentData) {
      setTaskReference(
        dispatch,
        `Attachment ${partnumber}`,
        attachmentData?.detail?.attachment_tree_node_id,
        attachmentData?.tree_node_id,
        attachmentData?.ltree_path
      );
    }
  }, [mode, attachmentData, dispatch, partnumber]);

  if (loading) {
    return <Loader text="Loading attachment data..." />;
  }

  return (
    <div>
      <AppBar
        onBack={handleAppBarBack}
        subTitleAboveTitle={
          <>
            {localStorage.getItem("user_name")} is{" "}
            {mode === "add" ? "Creating" : "Editing"}
          </>
        }
        title={pageTitle}
        showTablesDropdown={true}
        showProfileButton={fromVersionHistory ? false : true}
        buttons={
          fromVersionHistory
            ? []
            : [
              { label: "Preview", onClick: handlePreviewClick },
              {
                label: "Save",
                onClick: canEdit ? handleSaveClick : undefined,
                disabled: !canEdit,
                title: !canEdit
                  ? "You do not have permission to save attachments."
                  : undefined,
              },
            ]
        }
        subheading={breadcrumbText}
        showBlackTitle={true}
        showColorTitle={false}
        subheadingAfterTitle={mode === "edit" ? lastSavedText : undefined}
        versionHistoryHandler={
          mode === "edit" ? () => setShowVersionHistory(true) : null
        }
      />

      <TextField
        label="Version ID"
        value={mode === "edit" ? versionId : ""}
        readOnly
        width="30%"
        inputStyle={{ color: "#999999" }}
      />

      <div style={{
        marginLeft: "10px",
        marginRight: "10px"
      }}>
        <label
          style={{
            fontWeight: "bold",
            color: "#333",
            marginBottom: "8px",
            display: "block",
          }}
        >
          Short Description
        </label>
        <ShortDescriptionEditor
          value={formData.shortDescription}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, shortDescription: value }));
          }}
          onDirtyChange={handleShortDescriptionDirtyChange}
          placeholder="Enter short description"
          minHeight="100px"
        />
      </div>

      <FileUpload
        label="Attachment"
        files={files}
        onChange={handleFileChange}
        onDelete={handleDeleteFile}
        required={true}
        multiple={true}
        mode={mode}
        disabled={!canEdit}
      />

      <SaveModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSave={handleConfirmSave}
        title={`Save ${pageTitle}`}
        subtitle={breadcrumbText}
        editorName={localStorage.getItem("user_name") || "Unknown User"}
        comment={comment}
        onCommentChange={(e) => setComment(e.target.value)}
        isLoading={isLoading}
        showLoader={true}
      />

      {isLoading && (
        <Loader
          text={
            mode === "add" ? "Saving attachment..." : "Updating attachment..."
          }
        />
      )}

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          procedurePartNumber={partnumber}
          procedureRevision={revision}
          versionHistories={mergedVersionHistories}
          mode={mode}
          versionId={versionId}
          breadcrumbText={breadcrumbText}
          revisionTreeNodes={allNodesData}
          nodeId={attachmentData?.tree_node_id || attachment_id_ref || ""}
          nodeIdField="attachment_tree_node_id"
          nodeHistoryMatchIds={
            Array.isArray(attachmentVersionHistoryMatchIds) &&
            attachmentVersionHistoryMatchIds.length > 0
              ? attachmentVersionHistoryMatchIds
              : undefined
          }
          nodeHistoryLeafMediaOrdinal={
            attachmentMediaOrdinalForNodeHistory != null &&
            Number.isFinite(Number(attachmentMediaOrdinalForNodeHistory))
              ? Number(attachmentMediaOrdinalForNodeHistory)
              : undefined
          }
          nodeHistoryLeafMediaPhraseRegex="Attachment"
          procedureRevisionIdForNodeHistory={revisionId}
          restrictNodeHistoryToProcedureRevision={
            allVersionHistories.length === 0
          }
          emptyMessage={
            mode === "add"
              ? "No version history available for new attachments"
              : "No version history available"
          }
          initialFilterType="versions_for_this_page"
          onViewDetails={(row) => {
            const aid = row.audit_trail_id || row.audit_trail_id_ref;
            if (!aid) return;
            const path = getVersionHistoryAuditPreviewPath(
              aid,
              row.action || row.action_to_show
            );
            if (!path) return;
            setShowVersionHistory(false);
            navigate(path);
          }}
          isRowViewActionDisabled={(row) =>
            !(row.audit_trail_id || row.audit_trail_id_ref)
          }
          exportCsvDisabled={versionHistoryTableLoading}
        />
      )}
    </div>
  );
};

export default AttachmentForm;
